import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useCustomToast,
  VStack,
} from '@elkaso-app/web-design';

import { ChakraAsyncSelect } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useCreateAccessControlApi } from 'apis/crm-access-control-management/use-create-access-control-api';
import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FiUser, FiUsers } from 'react-icons/fi';

interface GrantAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  supplier: { value: number; label: string; data?: any } | null;
  user: { value: number; label: string; data?: any } | null;
}

const defaultValues: FormData = {
  supplier: null,
  user: null,
};

export const GrantAccessModal: React.FC<GrantAccessModalProps> = ({ isOpen, onClose }) => {
  const toast = useCustomToast();
  const [userOptions, setUserOptions] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const createAccessMutation = useCreateAccessControlApi({
    onSuccess: () => {
      toast({
        description: 'Access granted successfully!',
        status: 'success',
      });
      handleClose();
    },
  });

  // Form setup
  const { control, handleSubmit, reset, watch } = useForm<FormData>({
    defaultValues,
    mode: 'onChange',
  });

  const selectedSupplier = watch('supplier');
  const selectedUser = watch('user');

  // Load supplier options for ChakraAsyncSelect
  const loadSupplierOptions = useCallback((inputValue: string, callback: (options: any[]) => void) => {
    if (inputValue.length < 2) {
      callback([]);
      return;
    }

    // Make API call directly inside loadOptions (using same format as useSearchSuppliersApi)
    const filterValue = `name||$cont||${inputValue.trim()}`;
    http
      .get('suppliers', {
        params: {
          limit: '20',
          page: '1',
          filter: filterValue,
        },
      })
      .then((response) => {
        const options = (response.data?.data || []).map((supplier: any) => ({
          value: supplier.id,
          label: supplier.name,
          data: supplier,
        }));
        callback(options);
      })
      .catch((error) => {
        console.error('Error loading suppliers:', error);
        callback([]);
      });
  }, []);

  // Load users when supplier changes
  useEffect(() => {
    if (!selectedSupplier) {
      setUserOptions([]);
      return;
    }

    setLoadingUsers(true);
    const params: any = {
      page: 1,
      limit: 50,
      filter: `supplierId||$eq||${selectedSupplier.value}`,
    };

    http
      .get('users', { params })
      .then((response) => {
        const options = (response.data?.data || []).map((user: any) => ({
          value: user.id,
          label: `${user.name} (${user.email})`,
          data: user,
        }));
        setUserOptions(options);
      })
      .catch((error) => {
        console.error('Error loading users:', error);
        setUserOptions([]);
      })
      .finally(() => {
        setLoadingUsers(false);
      });
  }, [selectedSupplier]);

  // Load user options for ChakraAsyncSelect (for search filtering)
  const loadUserOptions = useCallback(
    (inputValue: string, callback: (options: any[]) => void) => {
      if (!selectedSupplier) {
        callback([]);
        return;
      }

      // If no search input, return pre-loaded options
      if (!inputValue || inputValue.trim().length < 2) {
        callback(userOptions);
        return;
      }

      // Make API call with search filter
      const params: any = {
        page: 1,
        limit: 50,
        filter: `supplierId||$eq||${selectedSupplier.value}&filter=name||$contL||${inputValue.trim()}`,
      };

      http
        .get('users', { params })
        .then((response) => {
          const options = (response.data?.data || []).map((user: any) => ({
            value: user.id,
            label: `${user.name} (${user.email})`,
            data: user,
          }));
          callback(options);
        })
        .catch((error) => {
          console.error('Error loading users:', error);
          callback([]);
        });
    },
    [selectedSupplier, userOptions]
  );

  const handleClose = useCallback(() => {
    reset(defaultValues);
    setUserOptions([]);
    setLoadingUsers(false);
    onClose();
  }, [reset, onClose]);

  const onSubmit = useCallback(
    async (data: FormData) => {
      if (!data.user?.value) {
        toast({
          description: 'Please select a user',
          status: 'error',
        });
        return;
      }

      try {
        await createAccessMutation.mutateAsync({
          userId: data.user.value,
        });
      } catch (error) {
        // Error is handled by the mutation hook
      }
    },
    [createAccessMutation, toast]
  );

  const isSubmitDisabled = !selectedSupplier || !selectedUser || createAccessMutation.isLoading;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='lg' closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <VStack align='start' spacing={1}>
            <HStack spacing={2}>
              <FiUsers />
              <Text>Grant Access Control</Text>
            </HStack>
            <Text fontSize='sm' fontWeight='normal' color='gray.600'>
              Grant brand access permissions to a user
            </Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />

        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalBody>
            <VStack spacing={6} align='stretch'>
              {/* Supplier Selection */}
              <Controller
                name='supplier'
                control={control}
                rules={{ required: 'Please select a supplier' }}
                render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                  <FormControl isInvalid={!!error}>
                    <FormLabel>
                      <HStack spacing={2}>
                        <FiUsers size={16} />
                        <Text>Supplier</Text>
                      </HStack>
                    </FormLabel>
                    <ChakraAsyncSelect
                      name={name}
                      value={value}
                      onChange={(newValue) => {
                        const typedValue = newValue as { value: number; label: string; data?: any } | null;
                        onChange(typedValue);
                        // Reset user selection when supplier changes
                        reset({
                          supplier: typedValue,
                          user: null,
                        });
                      }}
                      loadOptions={loadSupplierOptions}
                      defaultOptions={[]}
                      placeholder='Type to search suppliers...'
                      isClearable
                      noOptionsMessage={({ inputValue }) =>
                        (inputValue || '').length < 2 ? 'Type at least 2 characters' : 'No suppliers found'
                      }
                      cacheOptions={false}
                    />
                    <FormErrorMessage>{error?.message}</FormErrorMessage>
                  </FormControl>
                )}
              />

              {/* User Selection */}
              <Controller
                name='user'
                control={control}
                rules={{ required: 'Please select a user' }}
                render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                  <FormControl isInvalid={!!error}>
                    <FormLabel>
                      <HStack spacing={2}>
                        <FiUser size={16} />
                        <Text>User</Text>
                      </HStack>
                    </FormLabel>
                    <ChakraAsyncSelect
                      name={name}
                      value={value}
                      onChange={onChange}
                      loadOptions={loadUserOptions}
                      defaultOptions={userOptions}
                      placeholder={selectedSupplier ? 'Select a user or type to search...' : 'Select a supplier first'}
                      isClearable
                      isLoading={loadingUsers}
                      isDisabled={!selectedSupplier}
                      noOptionsMessage={() =>
                        !selectedSupplier ? 'Please select a supplier first' : 'No users found for this supplier'
                      }
                      cacheOptions={false}
                    />
                    <FormErrorMessage>{error?.message}</FormErrorMessage>
                  </FormControl>
                )}
              />

              {/* Preview Section */}
              {selectedSupplier && selectedUser && (
                <VStack spacing={3} p={4} bg='blue.50' borderRadius='md' align='start'>
                  <Text fontSize='sm' fontWeight='semibold' color='blue.800'>
                    Access Summary
                  </Text>
                  <VStack spacing={2} align='start' fontSize='sm'>
                    <HStack spacing={2}>
                      <Text fontWeight='medium' color='gray.700'>
                        Supplier:
                      </Text>
                      <Text color='gray.600'>{selectedSupplier.label}</Text>
                    </HStack>
                    <HStack spacing={2}>
                      <Text fontWeight='medium' color='gray.700'>
                        User:
                      </Text>
                      <Text color='gray.600'>{selectedUser.data?.name}</Text>
                    </HStack>
                    <HStack spacing={2}>
                      <Text fontWeight='medium' color='gray.700'>
                        Email:
                      </Text>
                      <Text color='gray.600'>{selectedUser.data?.email}</Text>
                    </HStack>
                  </VStack>
                  <Text fontSize='xs' color='blue.700' fontStyle='italic'>
                    This user will be granted brand access permissions
                  </Text>
                </VStack>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter>
            <HStack spacing={3}>
              <Button variant='outline' onClick={handleClose} isDisabled={createAccessMutation.isLoading}>
                Cancel
              </Button>
              <Button
                type='submit'
                colorScheme='green'
                isLoading={createAccessMutation.isLoading}
                loadingText='Granting Access...'
                isDisabled={isSubmitDisabled}>
                Grant Access
              </Button>
            </HStack>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};
